/**
 * The ticket conversation: public replies and internal notes, with the ticket's
 * change history collapsed under a single toggle. Internal notes get an amber
 * background and an "Internal note" label so a person never mistakes one for
 * something the requester can see.
 *
 * Comment HTML is rendered as the api returns it. The api sanitizes on write.
 */

import { useState } from 'react';
import type { SdAttachment } from '@ampersandbrands/ui';
import type { SdStaffComment, SdTicketEvent } from '@/lib/serviceDeskTypes';
import { isThumbnailCandidate } from '@/lib/serviceDeskFiles';
import { SD_BTN_GHOST, SD_MUTED } from './sdStyles';
import { TimelineComment } from './TimelineComment';
import { TimelineHistory } from './TimelineHistory';

const ATTACHMENT_LINK =
  'inline-flex items-center gap-1 rounded-md border border-amp-line px-2 py-1 text-[12px] text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';

function sizeLabel(a: SdAttachment): string {
  return `${Math.max(1, Math.round(a.size_bytes / 1024))} KB`;
}

/**
 * Image attachments of 10 KB or more show as a thumbnail (click opens the full
 * image in a new tab). Tiny images (usually signature icons) and every other
 * file stay as a link with the file name and size.
 */
export function AttachmentList({ items }: { items: SdAttachment[] }) {
  if (items.length === 0) return null;
  const thumbs = items.filter(isThumbnailCandidate);
  const links = items.filter((a) => !isThumbnailCandidate(a));
  return (
    <div className="mt-2 flex flex-col gap-2">
      {thumbs.length > 0 && (
        <ul className="flex flex-wrap items-start gap-3">
          {thumbs.map((a) => (
            <li key={a.id}>
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                title={`${a.filename} (${sizeLabel(a)})`}
                data-attachment-thumbnail
                className={`inline-flex max-w-[240px] flex-col gap-1 text-[12px] ${SD_MUTED}`}
              >
                <img
                  src={a.url}
                  alt={a.filename}
                  loading="lazy"
                  className="max-h-40 w-auto max-w-full rounded-md border border-amp-line object-contain dark:border-amp-dark-line"
                />
                <span className="truncate">
                  {a.filename} ({sizeLabel(a)})
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
      {links.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {links.map((a) => (
            <li key={a.id}>
              <a href={a.url} target="_blank" rel="noreferrer" className={ATTACHMENT_LINK}>
                {a.filename}
                <span className={SD_MUTED}>({sizeLabel(a)})</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TicketTimeline({
  comments,
  events,
}: {
  comments: SdStaffComment[];
  events: SdTicketEvent[];
}) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const sorted = [...comments].sort((x, y) => x.created_at.localeCompare(y.created_at));
  const allOpen = sorted.length > 0 && sorted.every((c) => expanded.has(c.id));

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col gap-3">
      <TimelineHistory events={events} />
      {sorted.length === 0 ? (
        <p className={`text-sm ${SD_MUTED}`}>No comments yet.</p>
      ) : (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              className={SD_BTN_GHOST}
              onClick={() => setExpanded(allOpen ? new Set() : new Set(sorted.map((c) => c.id)))}
            >
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
          <ol className="flex flex-col gap-3">
            {sorted.map((c) => (
              <TimelineComment key={c.id} c={c} expanded={expanded.has(c.id)} onToggle={() => toggle(c.id)} />
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
